import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Stack, Alert, Textarea } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { CodeableConceptInput } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual } from 'lodash';
import { ARKON } from '@consts/systems.consts';
import {
  getSurgeryCode,
  hasSurgeryCode,
  createNewSurgeryInputItem,
  SurgeryInputItem,
} from '@/utils/surgery.utils';

export interface ProcedureInputModalProps {
  opened: boolean;
  onClose: () => void;
  /** Function to save procedure to server. Returns the saved procedure with server-assigned ID. */
  onSave: (
    procedure: SurgeryInputItem,
    isMedicalHistory?: boolean
  ) => Promise<SurgeryInputItem>;
  /** Callback called after successful save. Used to update parent component state. */
  onSaveSuccess?: (procedure: SurgeryInputItem) => void;
  procedure?: SurgeryInputItem;
  mode: 'add' | 'edit';
  patient: Patient;
  encounter: Encounter | undefined;
  isMedicalHistory?: boolean;
}

// Default empty procedure for new entries
const createEmptyProcedure = (): SurgeryInputItem =>
  createNewSurgeryInputItem();

export const ProcedureInputModal = ({
  opened,
  onClose,
  onSave,
  onSaveSuccess,
  procedure,
  mode,
  encounter,
  isMedicalHistory = false,
}: ProcedureInputModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state - using ProcedureInputItem directly
  const [formData, setFormData] = useState<SurgeryInputItem>(
    createEmptyProcedure()
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Key to force CodeableConceptInput remount on each modal open
  const [inputKey, setInputKey] = useState(0);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<SurgeryInputItem | null>(null);
  const baselineDatesRef = useRef<{ performed: Date | null } | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  // Date state (separate because FHIR uses strings, form uses Date)
  const [performedDate, setPerformedDateState] = useState<Date | null>(null);

  // Reset form when modal opens or procedure changes
  useEffect(() => {
    if (opened) {
      // Increment key to force CodeableConceptInput to remount with fresh defaultValue
      setInputKey((prev) => prev + 1);
      setValidationError(null);
      setBaselineSet(true); // Flag to capture baseline after state is set
      if (procedure) {
        setFormData({ ...procedure });
        setPerformedDateState(procedure.performedDate);
      } else {
        const empty = createEmptyProcedure();
        setFormData(empty);
        setPerformedDateState(null);
      }
    } else {
      setBaselineSet(false);
    }
  }, [opened, procedure]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formData };
      baselineDatesRef.current = { performed: performedDate };
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, performedDate]);

  // Update formData when date changes
  const updateDate = useCallback((date: Date | null) => {
    setFormData((prev) => ({ ...prev, performedDate: date }));
  }, []);

  useEffect(() => {
    updateDate(performedDate);
  }, [performedDate, updateDate]);

  // Clear validation error on any change
  const clearValidation = () => {
    if (validationError) {
      setValidationError(null);
    }
  };

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      const formChanged = !isEqual(formData, baselineRef.current);
      const datesChanged = !isEqual(
        { performed: performedDate },
        baselineDatesRef.current
      );
      return formChanged || datesChanged;
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData({ ...baselineRef.current });
    }
    if (baselineDatesRef.current) {
      setPerformedDateState(baselineDatesRef.current.performed);
    }
    setInputKey((prev) => prev + 1); // Reset CodeableConceptInput
    setValidationError(null);
  };

  const handleSave = async () => {
    // Validate encounter
    if (!encounter) {
      setValidationError(
        t('surgeries.encounterRequired', {
          defaultValue: 'Encounter is required to save a procedure',
        })
      );
      return;
    }

    // Validate required fields
    if (!hasSurgeryCode(formData)) {
      setValidationError(
        t('surgeries.surgeryRequired', {
          defaultValue: 'Please select or enter a procedure',
        })
      );
      return;
    }

    // Save to server
    setSaving(true);
    try {
      const savedProcedure = await onSave(formData, isMedicalHistory);
      // Notify parent component of successful save
      if (onSaveSuccess) {
        onSaveSuccess(savedProcedure);
      }
      onClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t('surgeries.saveErrorTitle', {
          defaultValue: 'Failed to save procedure',
        }),
        message: String(error),
      });
    } finally {
      setSaving(false);
    }
  };

  // Extract current values using getters
  const currentCode = getSurgeryCode(formData);

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('surgeries.addProcedure', {
              defaultValue: 'Add Procedure',
            })
          : t('surgeries.editProcedure', {
              defaultValue: 'Edit Procedure',
            })
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Validation Error */}
        {validationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {validationError}
          </Alert>
        )}

        {/* Procedure Code/Name */}
        <CodeableConceptInput
          key={`code-${inputKey}`}
          path="Procedure.code"
          binding={ARKON.VS.PROCEDURES_EL}
          label={t('surgeries.surgeryName', {
            ns: 'patient-pages',
            defaultValue: 'Procedure',
          })}
          name="procedure-code"
          maxValues={1}
          defaultValue={currentCode}
          onChange={(code) => {
            setFormData((prev) => ({ ...prev, surgery: code }));
            clearValidation();
          }}
          outcome={undefined}
          placeholder={t('surgeries.surgeryPlaceholder', {
            ns: 'patient-pages',
            defaultValue: 'e.g. Appendectomy',
          })}
          creatable={true}
          required
        />

        {/* Performed Date */}
        <DateInputWrapper
          name="performedDate"
          label={t('surgeries.performedDate', {
            ns: 'patient-pages',
            defaultValue: 'Date Performed',
          })}
          value={performedDate}
          onChange={(date) => {
            setPerformedDateState(date);
            clearValidation();
          }}
        />

        {/* Notes */}
        <Textarea
          label={t('surgeries.notes', {
            ns: 'patient-pages',
            defaultValue: 'Notes',
          })}
          placeholder={t('surgeries.notesPlaceholder', {
            ns: 'patient-pages',
            defaultValue: 'Additional notes about this procedure...',
          })}
          value={formData.notes}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, notes: e.currentTarget.value }));
            clearValidation();
          }}
          minRows={2}
          maxRows={4}
        />

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={
            mode === 'add'
              ? t('surgeries.addButton', {
                  defaultValue: 'Add',
                })
              : t('surgeries.saveButton', {
                  defaultValue: 'Save',
                })
          }
        />
      </Stack>
    </Modal>
  );
};
